import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import MultiSelect from '../../components/common/MultiSelect';
import {
  useFetchCitiesQuery,
  useFetchCountriesQuery,
  useFetchStatesQuery,
  useGetW9TaxFormMutation,
  useSubmitAffiliateFormMutation,
} from '../../redux/apiSlice';
import { useNavigate } from 'react-router-dom';
import { CircleChevronLeft, Loader2 } from 'lucide-react';
import Button from '../../components/common/Button';
import { toast } from 'react-toastify';
import { affiliateSchema } from '../../validations/SchemaValidations';
import { expertiseOptions } from '../../components/Footer/data';
import { businessOptions, genderOptions } from '../../components/Signup/data';

interface FormData {
  first_name: string;
  last_name: string;
  age: number | null;
  gender: number | null;
  country: string;
  business: string;
  country_code: string | undefined | null;
  state: string;
  city: string;
  postal_code: number | null;
  address: string;
  email: string;
  expertise: string[];
  platforms: string[];
  website_url: string;
  linked_in: string;
  facebook?: string;
  instagram?: string;
  twitter?: string;
  youtube?: string;
  tiktok?: string;
  audience_size?: number | null;
  referral_source?: string;
  dob?: string;
}

const AffiliateForm: React.FC = () => {
  const {
    data: countries,
    isError: countriesError,
    isLoading: countriesLoading,
  } = useFetchCountriesQuery();
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [taxFormId, setTaxFormId] = useState<string>('');

  const [selectedState, setSelectedState] = useState<string>('');

  const [, setSelectedCity] = useState<string>('');

  const {
    data: states,
    isError: statesError,
    isLoading: statesLoading,
  } = useFetchStatesQuery(selectedCountry, { skip: !selectedCountry });
  const {
    data: cities,
    isError: citiesError,
    isLoading: citiesLoading,
  } = useFetchCitiesQuery(selectedState, { skip: !selectedState });

  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    age: null,
    gender: null,
    business: '',
    country: '',
    country_code: '',
    state: '',
    city: '',
    postal_code: null,
    address: '',
    email: '',
    expertise: [],
    platforms: [],
    website_url: '',
    linked_in: '',
    facebook: '',
    instagram: '',
    twitter: '',
    youtube: '',
    tiktok: '',
    audience_size: null,
    referral_source: '',
    dob: '',
  });

  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof FormData, string>>
  >({});
  console.log(formErrors, 'errors');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submitAffiliateForm, { isLoading }] = useSubmitAffiliateFormMutation();
  const [getW9TaxForm, { data }] = useGetW9TaxFormMutation();

  // Function to calculate age from date of birth
  const calculateAge = (dob: string): number | null => {
    if (!dob) return null;

    const birthDate = new Date(dob);
    const today = new Date();

    // Check if the date is valid
    if (isNaN(birthDate.getTime())) return null;

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // If birthday hasn't occurred this year, subtract 1
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age >= 0 ? age : null;
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        [name]:
          name === 'audience_size' ? parseInt(value, 10) || undefined : value,
      };

      // Calculate age when DOB changes
      if (name === 'dob') {
        const calculatedAge = calculateAge(value);
        updatedData.age = calculatedAge;
      }

      return updatedData;
    });

    try {
      await affiliateSchema.validateAt(name, { [name]: value });
      setFormErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          [name]: error.message,
        }));
      }
    }
  };

  const handleSelectChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
    field: keyof FormData,
  ) => {
    const selectedOption = e.target.selectedOptions[0];
    const value = e.target.value;
    const data_id = selectedOption?.getAttribute('data-id');
    const data_iso = selectedOption?.getAttribute('data-iso');

    setFormData((prev) => {
      const updatedFormData = { ...prev, [field]: value };

      if (field === 'country') {
        return { ...updatedFormData, state: '', city: '' };
      } else if (field === 'state') {
        return { ...updatedFormData, city: '' };
      }

      return updatedFormData;
    });

    // Handle selected country
    if (field === 'country' && data_id && value) {
      setSelectedCountry(data_id);
      setFormData((prev) => ({ ...prev, country_code: data_iso }));
      setSelectedState('');
      setSelectedCity('');
    }
    // Handle selected state
    else if (field === 'state' && data_id && value) {
      setSelectedState(data_id);
      setSelectedCity('');
    }
    // Handle selected city
    else if (field === 'city' && data_id && value) {
      setSelectedCity(data_id);
    }

    // Validate the field and update errors
    try {
      await affiliateSchema.validateAt(field, { [field]: value });
      setFormErrors((prevErrors) => ({ ...prevErrors, [field]: undefined }));
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          [field]: error.message,
        }));
      }
    }
  };

  const handleMultiSelectChange = (
    name: 'expertise' | 'platforms',
    selected: string[],
  ) => {
    setFormData({ ...formData, [name]: selected });

    affiliateSchema
      .validateAt(name, { [name]: selected })
      .then(() => {
        setFormErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
      })
      .catch((error) => {
        if (error instanceof Yup.ValidationError) {
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            [name]: error.message,
          }));
        }
      });
  };
  // useEffect(() => {
  //   if (isSuccess) {
  //     console.log('affliate created successfully');
  //     getW9TaxForm();
  //     window.open(process.env.REACT_APP_TROOLEY_TAX_FORM_URL, '_blank');
  //   }
  // }, [isSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    try {
      console.log('Validating form data:', formData);
      await affiliateSchema.validate(formData, { abortEarly: false });

      if (linkErrors) {
        console.error('Link errors detected:', linkErrors);
        return;
      }
      setFormErrors({});

      const business =
        businessOptions.find((option) => option.id === formData.business)
          ?.value || formData.business;

      // Prepare the payload
      const payload = {
        ...formData,
        business,
      };

      console.log('Submitting affiliate form with payload:', payload);
      const response: any = await submitAffiliateForm(payload);
      if (response?.data?.status_code === 201) {
        const { recipient_id, recipient_email } = response.data;
        console.log('Form submission successful. Fetching tax form...', {
          recipient_id,
          recipient_email,
        });

        const taxFormResponse = await getW9TaxForm({
          affiliateId: recipient_id,
          email: recipient_email.toLowerCase(),
          firstName: formData?.first_name,
          street1: formData?.address,
          street2: formData?.address,
          city: formData?.city,
          postalCode: formData?.postal_code,
          region: 'name', // Replace with valid region data
          country: formData.country,
        }).unwrap();

        console.log('Tax form response:', taxFormResponse);

        // Uncomment to open widget URL
        // window.open(taxFormResponse.widget_url, '_blank');

        // toast.success(
        //   'My Code Dragon has partnered with Trolley for our affiliate program, please wait until the Trolley portal opens.',
        // );
      } else if (response?.error === 400) {
        console.error('Error: Affiliate application already exists.');
        toast.error(
          response?.error?.data?.messages ||
            'Affiliate application with this email already exists.',
        );
        navigate('/');
      } else {
        console.error('Unexpected API response:', response);
        throw new Error(
          response?.error?.data?.messages || 'Unknown error during submission',
        );
      }
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        const errors: Partial<Record<keyof FormData, string>> = {};
        error.inner.forEach((err) => {
          if (err.path) errors[err.path as keyof FormData] = err.message;
        });
        console.error('Validation errors:', errors);
        setFormErrors(errors);
      } else {
        console.error('Unexpected error:', error);
        toast.error(
          error?.data?.error ||
            'An unexpected error occurred. Please try again later.',
        );
      }
    }
  };

  const hasErrors = Object.keys(formErrors).length > 0;
  const linkErrors =
    !formData.website_url &&
    !formData.linked_in &&
    !formData.facebook &&
    !formData.instagram &&
    !formData.twitter &&
    !formData.youtube &&
    !formData.tiktok;
  console.log('widget ur', data);
  //To be run on the front end

  // (function () {
  //   window.addEventListener('message', function (e) {
  //     const widgetEvent = e.data;
  //     if (widgetEvent.event === 'document.loaded') {
  //       // The document has been loaded
  //       console.log(
  //         `Document Loaded. Name of the event - ${widgetEvent.event}`,
  //       );
  //     } else if (widgetEvent.event === 'document.failed') {
  //       // The document failed to load
  //       console.log(
  //         `Document failed to load. Name of the event - ${widgetEvent.event}.`,
  //       );
  //       console.log(`Error Message: "${widgetEvent.document.message}".`);
  //       console.log(`Error Code: "${widgetEvent.document.code}"`);
  //     } else if (widgetEvent.event === 'module.loaded') {
  //       // A module has been loaded
  //       console.log(`${widgetEvent.module[0]} module loaded.`);
  //     } else if (widgetEvent.event === 'module.successful') {
  //       // A module has submitted its data successfully
  //       console.log(`${widgetEvent.module[0]} module submitted successfully.`);
  //     } else if (widgetEvent.event === 'module.failed') {
  //       // A module failed to submitted it's data
  //       console.log(`${widgetEvent.module[0]} module failed.`);
  //     }
  //     if (widgetEvent.event === 'taxForm.submitted') {
  //       setTaxFormId(widgetEvent.taxForm.id);
  //     }
  //   });
  // })();

  // useEffect(() => {
  //   if (taxFormId) {
  //     navigate('/');
  //     toast.success('W9 Form Submitted Successfully.');
  //   }
  // }, [taxFormId]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const handleMessage = (e: any) => {
      const widgetEvent = e.data;
      if (widgetEvent.event === 'taxForm.submitted') {
        clearTimeout(timer); // Clear previous timeout
        timer = setTimeout(() => {
          setTaxFormId(widgetEvent.taxForm.id);
          toast.success(
            'Your affiliate application has been successfully submitted! You will receive an email shortly.',
          );
        }, 300); // Delay of 300ms to debounce
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (taxFormId) {
      navigate('/');
    }
  }, [taxFormId]);

  console.log('formErrors', formErrors, linkErrors);

  return (
    <>
      {data?.widget_url ? (
        <div className="mt-8 ">
          <iframe
            src={data?.widget_url}
            title="Affiliate Program Application"
            className="w-[80%] h-[80vh] border border-gray-300 rounded-md m-auto"
            allowFullScreen
          ></iframe>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center min-h-screen p-6 ">
          <div className="w-full max-w-4xl shadow-[0_0_15px_rgba(0,0,0,0.2)] p-8 md:p-12 rounded-lg">
            <div className="flex items-center mb-8">
              <CircleChevronLeft
                onClick={() => navigate(-1)}
                className="w-8 h-8 mr-4 text-gray-600 cursor-pointer hover:text-gray-800"
              />
              <h1 className="text-2xl md:text-3xl font-bold text-gray-700">
                Affiliate Program Application
              </h1>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2  gap-6 mb-10">
                <InputField
                  id="first_name"
                  name="first_name"
                  label="First Name"
                  placeholder="First name"
                  value={formData.first_name}
                  onChange={handleChange}
                  error={formErrors.first_name}
                />
                <InputField
                  id="last_name"
                  name="last_name"
                  label="Last Name"
                  placeholder="Last name"
                  value={formData.last_name}
                  onChange={handleChange}
                  error={formErrors.last_name}
                />

                <InputField
                  id="email"
                  name="email"
                  label="Email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  error={formErrors.email}
                />

                <div>
                  <label
                    htmlFor="business"
                    className="block font-medium text-gray-700 mb-2"
                  >
                    Business (Optional)
                  </label>
                  <select
                    id="business"
                    name="business"
                    value={formData.business || ''} // Save ID in the state
                    onChange={(e) => handleSelectChange(e, 'business')}
                    className="w-full px-4 py-[.7rem] border border-gray-300 rounded-md bg-white"
                  >
                    <option value="">Select Business Type</option>
                    {businessOptions.map((item) => (
                      <option
                        key={item.id}
                        value={item.id} // Use numeric ID as the value
                      >
                        {item.label} {/* Show name in the dropdown */}
                      </option>
                    ))}
                  </select>
                  {formErrors.business && (
                    <p className="text-red-500 text-sm mt-2">
                      {formErrors.business}
                    </p>
                  )}
                </div>

                {/* Gender Dropdown */}
                <div>
                  <label
                    htmlFor="gender"
                    className="block font-medium text-gray-700 mb-2"
                  >
                    Gender
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender || ''} // Save ID in the state
                    onChange={(e) => handleSelectChange(e, 'gender')}
                    className="w-full px-4 py-[.7rem] border border-gray-300 rounded-md bg-white"
                  >
                    <option value="">Select a Gender</option>
                    {genderOptions.map((gender) => (
                      <option
                        key={gender.id}
                        value={gender.id} // Use numeric ID as the value
                      >
                        {gender.label} {/* Show name in the dropdown */}
                      </option>
                    ))}
                  </select>
                  {formErrors.gender && (
                    <p className="text-red-500 text-sm mt-2">
                      {formErrors.gender}
                    </p>
                  )}
                </div>

                {/* Country Dropdown */}
                <div>
                  <label
                    htmlFor="country"
                    className="block font-medium text-gray-700 mb-2"
                  >
                    Country<span className="text-red-500">*</span>
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={(e) => handleSelectChange(e, 'country')}
                    className="w-full px-4 py-[.7rem] border border-gray-300 rounded-md bg-white"
                    disabled={countriesLoading}
                  >
                    <option value="">
                      {countriesLoading
                        ? 'Loading countries...'
                        : 'Select a country'}
                    </option>
                    {countries?.map((country: any) => (
                      <option
                        key={country.id}
                        value={country.name}
                        data-id={country.id}
                        data-iso={country.code}
                      >
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {countriesError && (
                    <p className="text-red-500 text-sm mt-2">
                      Failed to load countries. Please try again.
                    </p>
                  )}
                  {formErrors.country && (
                    <p className="text-red-500 text-sm mt-2">
                      {formErrors.country}
                    </p>
                  )}
                </div>

                {/* State Dropdown */}
                <div>
                  <label
                    htmlFor="state"
                    className="block font-medium text-gray-700 mb-2"
                  >
                    State<span className="text-red-500">*</span>
                  </label>
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={(e) => handleSelectChange(e, 'state')}
                    className="w-full px-4 py-[.7rem] border border-gray-300 rounded-md bg-white"
                    disabled={!selectedCountry || statesLoading}
                  >
                    <option value="">
                      {!selectedCountry
                        ? 'Please select a country first'
                        : statesLoading
                          ? 'Loading states...'
                          : 'Select a state'}
                    </option>
                    {states?.map((state: any) => (
                      <option
                        key={state.id}
                        value={state.name}
                        data-id={state.id}
                      >
                        {state.name}
                      </option>
                    ))}
                  </select>
                  {statesError && selectedCountry && (
                    <p className="text-red-500 text-sm mt-2">
                      Failed to load states. Please try again.
                    </p>
                  )}
                  {formErrors.state && (
                    <p className="text-red-500 text-sm mt-2">
                      {formErrors.state}
                    </p>
                  )}
                </div>

                {/* City Dropdown */}
                <div>
                  <label
                    htmlFor="city"
                    className="block font-medium text-gray-700 mb-2"
                  >
                    City<span className="text-red-500">*</span>
                  </label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={(e) => handleSelectChange(e, 'city')}
                    className="w-full px-4 py-[.7rem] border border-gray-300 rounded-md bg-white"
                    disabled={!selectedState || citiesLoading}
                  >
                    <option value="">
                      {!selectedState
                        ? 'Please select a state first'
                        : citiesLoading
                          ? 'Loading cities...'
                          : 'Select a city'}
                    </option>
                    {cities?.map((city: any) => (
                      <option key={city.id} value={city.name} data-id={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>

                  {citiesError && selectedState && (
                    <p className="text-red-500 text-sm mt-2">
                      Failed to load cities. Please try again.
                    </p>
                  )}
                  {formErrors.city && (
                    <p className="text-red-500 text-sm mt-2">
                      {formErrors.city}
                    </p>
                  )}
                </div>

                <InputField
                  id="postal_code"
                  name="postal_code"
                  label="Postal Code"
                  placeholder="Postal Code"
                  // value={formData.postal_code || null}
                  value={formData.postal_code?.toString() ?? ''}
                  onChange={handleChange}
                  error={formErrors.postal_code}
                />
                <InputField
                  id="address"
                  name="address"
                  label="Address"
                  placeholder="Address"
                  value={formData.address}
                  onChange={handleChange}
                  error={formErrors.address}
                />
                <div>
                  <label
                    htmlFor="dob"
                    className="block font-medium text-gray-700 mb-2"
                  >
                    Date of Birth
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="dob"
                    name="dob"
                    type="date"
                    placeholder="Date of Birth"
                    value={formData.dob}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg text-gray-800 ${formErrors.dob ? 'border-red-500' : 'border-gray-300'} focus:outline-none`}
                  />
                  {formErrors.dob && (
                    <p className="text-red-500 text-sm mt-2">
                      {formErrors.dob}
                    </p>
                  )}
                </div>

                {/* Age Field - Calculated from DOB */}
                <div>
                  <label
                    htmlFor="age"
                    className="block font-medium text-gray-700 mb-2"
                  >
                    Age
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="age"
                    name="age"
                    type="text"
                    value={
                      formData.age !== null ? `${formData.age} years old` : ''
                    }
                    readOnly
                    className="w-full px-4 py-[.7rem] border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                    placeholder="Age will be calculated from date of birth"
                  />
                  {formErrors.age && (
                    <p className="text-red-500 text-sm mt-2">
                      {formErrors.age}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 mb-10">
                <MultiSelect
                  label="Area of Expertise"
                  options={expertiseOptions}
                  selectedOptions={formData.expertise}
                  onChange={(selected) =>
                    handleMultiSelectChange('expertise', selected)
                  }
                  columns={2}
                  error={formErrors.expertise}
                />
                {/* <MultiSelect
      label="Promotion Platforms"
      options={promotionPlatformsOptions}
      selectedOptions={formData.platforms}
      onChange={(selected) =>
        handleMultiSelectChange('platforms', selected)
      }
      columns={2}
      error={formErrors.platforms}
    /> */}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                <InputField
                  id="website_url"
                  name="website_url"
                  label="Website/Blog"
                  placeholder="Website link"
                  value={formData.website_url}
                  onChange={handleChange}
                  error={formErrors.website_url}
                />
                <InputField
                  id="linked_in"
                  name="linked_in"
                  label="LinkedIn"
                  placeholder="LinkedIn profile link"
                  value={formData.linked_in}
                  onChange={handleChange}
                  error={formErrors.linked_in}
                />
                <InputField
                  id="facebook"
                  name="facebook"
                  label="Facebook"
                  placeholder="Facebook profile link"
                  value={formData.facebook ?? ''}
                  onChange={handleChange}
                  error={formErrors.facebook}
                />
                <InputField
                  id="instagram"
                  name="instagram"
                  label="Instagram"
                  placeholder="Instagram profile link"
                  value={formData.instagram ?? ''}
                  onChange={handleChange}
                  error={formErrors.instagram}
                />
                <InputField
                  id="twitter"
                  name="twitter"
                  label="Twitter"
                  placeholder="Twitter profile link"
                  value={formData.twitter ?? ''}
                  onChange={handleChange}
                  error={formErrors.twitter}
                />
                <InputField
                  id="youtube"
                  name="youtube"
                  label="YouTube"
                  placeholder="YouTube channel link"
                  value={formData.youtube ?? ''}
                  onChange={handleChange}
                  error={formErrors.youtube}
                />
                <InputField
                  id="tiktok"
                  name="tiktok"
                  label="TikTok"
                  placeholder="tikTok channel link"
                  value={formData.tiktok ?? ''}
                  onChange={handleChange}
                  error={formErrors.tiktok}
                />
                <InputField
                  id="audience_size"
                  name="audience_size"
                  label="Audience Size"
                  placeholder="Audience size"
                  value={formData.audience_size?.toString() ?? ''}
                  onChange={handleChange}
                  error={formErrors.audience_size}
                />
                <InputField
                  id="referral_source"
                  name="referral_source"
                  label="Referral Source"
                  placeholder="How did you hear about us?"
                  value={formData.referral_source ?? ''}
                  onChange={handleChange}
                  error={formErrors.referral_source}
                />
              </div>
              {hasSubmitted && hasErrors && (
                <p className="text-red-500 text-center mb-4">
                  Please complete all required fields.
                </p>
              )}
              {hasSubmitted && linkErrors && (
                <p className="text-red-500 text-center mb-4">
                  Atleast one link is required to submit the application.
                </p>
              )}

              <div className="flex justify-center">
                <Button
                  type="submit"
                  className="w-full md:w-1/3 h-12 font-semibold text-lg flex items-center justify-center text-white transition"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="animate-spin h-6 w-6" />
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

interface InputFieldProps {
  id: string;
  name: string;
  label: string;
  placeholder?: string;
  value: string | number | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
}

const InputField: React.FC<InputFieldProps> = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  error,
}) => {
  const isRequired = [
    'first_name',
    'last_name',
    'country_code',
    'country',
    'city',
    'postal_code',
    'address',
    'email',
  ].includes(name);

  return (
    <div>
      <label htmlFor={id} className="block font-medium text-gray-700 mb-2">
        {label}
        {isRequired && <span className="text-red-500">*</span>}{' '}
      </label>
      <input
        id={id}
        name={name}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`w-full px-4 py-2 border rounded-lg text-gray-800 ${error ? 'border-red-500' : 'border-gray-300'} focus:outline-none`}
      />
      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
    </div>
  );
};

export default AffiliateForm;
